/**
 * AppICEMAP — LoginScreen
 * Custom element: `<login-screen>`
 *
 * Full-screen login form with email + password fields,
 * password visibility toggle, loading state, error display,
 * and links to register / continue as guest.
 *
 * Events:
 *   auth:success   → { user }
 *   auth:close     → { }
 */

import { login } from "../../api/auth.js";
import { dispatch } from "../../store/index.js";
import router from "../../router.js";
// Shadow DOM encapsulates styles — global auth.css can't reach inside, so we
// inline it and inject it into the shadow root. Design tokens still inherit.
import authCss from "../../../css/components/auth.css?inline";
// Phosphor's `.ph` rules live at document scope and don't cross the shadow
// boundary either — without this the back arrow, logo pin and password-eye
// icons all render as blank boxes.
import iconCss from "../../../css/utilities/phosphor-shadow.css?inline";

const template = document.createElement("template");
template.innerHTML = `
  <div class="auth-screen">
    <button class="auth-screen__back" aria-label="Volver" data-action="back">
      <i class="ph ph-caret-left" aria-hidden="true"></i>
    </button>

    <div class="auth-screen__logo">
      <i class="ph ph-map-pin" aria-hidden="true"></i>
      <h1>AppICEMAP</h1>
      <p>Tu mapa comunitario</p>
    </div>

    <form class="auth-form" novalidate>
      <div class="field" data-field="email">
        <label for="auth-email">Correo electrónico</label>
        <input
          id="auth-email"
          type="email"
          class="input"
          autocomplete="email"
          placeholder="tu@correo.com"
          required
        />
        <span class="field__error" role="alert" hidden></span>
      </div>

      <div class="field" data-field="password">
        <label for="auth-password">Contraseña</label>
        <div class="input-group">
          <input
            id="auth-password"
            type="password"
            class="input"
            autocomplete="current-password"
            placeholder="••••••••"
            required
          />
          <button type="button" class="input-group__toggle" aria-label="Mostrar contraseña" data-action="toggle-password">
            <i class="ph ph-eye" aria-hidden="true"></i>
          </button>
        </div>
        <span class="field__error" role="alert" hidden></span>
      </div>

      <div class="field__error form-error" role="alert" hidden></div>

      <button type="submit" class="btn-block">
        <span class="btn__label">Iniciar sesión</span>
        <span class="btn__spinner" aria-hidden="true"></span>
      </button>
    </form>

    <div class="auth-form__separator">
      <span>o</span>
    </div>

    <button class="btn-ghost-block" data-action="guest" style="width:100%;max-width:360px;">
      Continuar sin cuenta
    </button>

    <p class="auth-screen__footer">
      ¿No tienes cuenta?
      <a href="#register" data-action="go-register">Registrarse</a>
    </p>
  </div>
`;

export class LoginScreen extends HTMLElement {
	constructor() {
		super();
		this.attachShadow({ mode: "open" });
		const style = document.createElement("style");
		style.textContent = `${iconCss}\n${authCss}`;
		this.shadowRoot.appendChild(style);
		this.shadowRoot.appendChild(template.content.cloneNode(true));

		this._form = this.shadowRoot.querySelector(".auth-form");
		this._submitBtn = this.shadowRoot.querySelector(".btn-block");
		this._formError = this.shadowRoot.querySelector(".form-error");
		this._emailField = this.shadowRoot.querySelector('[data-field="email"]');
		this._passwordField = this.shadowRoot.querySelector(
			'[data-field="password"]',
		);
		this._emailInput = this.shadowRoot.querySelector("#auth-email");
		this._passwordInput = this.shadowRoot.querySelector("#auth-password");
		this._toggleBtn = this.shadowRoot.querySelector(
			'[data-action="toggle-password"]',
		);
		this._passwordVisible = false;
	}

	connectedCallback() {
		this._form.addEventListener("submit", this._onSubmit.bind(this));
		this._toggleBtn.addEventListener(
			"click",
			this._onTogglePassword.bind(this),
		);
		this.shadowRoot
			.querySelector('[data-action="back"]')
			?.addEventListener("click", () => this._close());
		this.shadowRoot
			.querySelector('[data-action="go-register"]')
			?.addEventListener("click", (e) => {
				e.preventDefault();
				router.navigate("/register");
			});
		this.shadowRoot
			.querySelector('[data-action="guest"]')
			?.addEventListener("click", () => this._close());
	}

	disconnectedCallback() {
		this._form.removeEventListener("submit", this._onSubmit);
	}

	/* ── Public API ──────────────────────────────────────────────────────── */

	/** Show the login screen */
	show() {
		this.style.display = "block";
		this._clearErrors();
		this._emailInput.value = "";
		this._passwordInput.value = "";
		this._passwordVisible = false;
		this._passwordInput.type = "password";
		this._toggleBtn.innerHTML = '<i class="ph ph-eye" aria-hidden="true"></i>';
	}

	/** Hide the login screen */
	hide() {
		this.style.display = "none";
	}

	/* ── Private ─────────────────────────────────────────────────────────── */

	_onTogglePassword() {
		this._passwordVisible = !this._passwordVisible;
		this._passwordInput.type = this._passwordVisible ? "text" : "password";
		this._toggleBtn.innerHTML = this._passwordVisible
			? '<i class="ph ph-eye-slash" aria-hidden="true"></i>'
			: '<i class="ph ph-eye" aria-hidden="true"></i>';
	}

	async _onSubmit(e) {
		e.preventDefault();
		this._clearErrors();

		const email = this._emailInput.value.trim();
		const password = this._passwordInput.value;

		// Validation
		let hasError = false;

		if (!email) {
			this._showFieldError(this._emailField, "Ingresa tu correo electrónico");
			hasError = true;
		} else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
			this._showFieldError(this._emailField, "Ingresa un correo válido");
			hasError = true;
		}

		if (!password) {
			this._showFieldError(this._passwordField, "Ingresa tu contraseña");
			hasError = true;
		}

		if (hasError) return;

		// Submit
		this._setLoading(true);

		try {
			const user = await login(email, password);

			// Store user in state
			dispatch("SET_USER", {
				id: user.id,
				email: user.email,
				first_name: user.first_name,
				last_name: user.last_name,
				avatar: user.avatar,
				karma: user.karma ?? 0,
				strikes: user.strikes ?? 0,
				rehabilitations: user.rehabilitations ?? 0,
				is_limited: user.is_limited ?? false,
				// Needed by utils/permissions.js — without these the admin
				// check can never see anything and the delete actions stay hidden
				role: user.role ?? null,
				policies: user.policies ?? null,
				admin_access: user.admin_access ?? undefined,
			});

			this.dispatchEvent(
				new CustomEvent("auth:success", {
					detail: { user },
					bubbles: true,
					composed: true,
				}),
			);

			this.hide();
			router.navigate("/");
		} catch (err) {
			console.error("[LoginScreen] Login failed:", err);
			const msg =
				err?.errors?.[0]?.message ||
				err?.message ||
				"Correo o contraseña incorrectos";
			this._showFormError(msg);
		} finally {
			this._setLoading(false);
		}
	}

	_showFieldError(fieldEl, message) {
		fieldEl.classList.add("field--error");
		const errorEl = fieldEl.querySelector(".field__error");
		if (errorEl) {
			errorEl.textContent = message;
			errorEl.hidden = false;
		}
	}

	_showFormError(message) {
		this._formError.textContent = message;
		this._formError.hidden = false;
	}

	_clearErrors() {
		[this._emailField, this._passwordField].forEach((f) =>
			f?.classList.remove("field--error"),
		);
		const errors = this.shadowRoot.querySelectorAll(".field__error");
		errors.forEach((e) => {
			e.textContent = "";
			e.hidden = true;
		});
		this._formError.hidden = true;
	}

	_setLoading(loading) {
		if (loading) {
			this._submitBtn.classList.add("is-loading");
			this._submitBtn.disabled = true;
		} else {
			this._submitBtn.classList.remove("is-loading");
			this._submitBtn.disabled = false;
		}
	}

	_close() {
		this.hide();
		this.dispatchEvent(
			new CustomEvent("auth:close", { bubbles: true, composed: true }),
		);
	}
}

if (!customElements.get("login-screen")) {
	customElements.define("login-screen", LoginScreen);
}
